import React, { useEffect, useMemo, useState } from 'react'
import { useMap } from 'react-leaflet'

import { ClusterMapMarker } from './ClusterMapMarker'
import {
  buildMarkerClusters,
  type ClusterableMarker,
  type ClusterZoomViewport,
  type MarkerClusterLayout,
} from './markerClustering'

/**
 * Owns zoom-driven cluster recomputation so pan/zoom does not re-render
 * the whole ControlCenterMap (toolbar, omni, panels, etc.).
 *
 * Layer-pixel distances are pan-invariant — only zoomend rebuilds clusters.
 */
export function MapClusterLayer({
  markers,
  excludeKeys,
  viewport,
  onSelectMember,
  children,
}: {
  markers: ClusterableMarker[]
  excludeKeys?: ReadonlySet<string>
  viewport: ClusterZoomViewport
  onSelectMember: (markerKey: string) => void
  children: (layout: MarkerClusterLayout) => React.ReactNode
}) {
  const map = useMap()
  const [zoomVersion, setZoomVersion] = useState(0)

  useEffect(() => {
    const bump = () => setZoomVersion((v) => v + 1)
    map.on('zoomend', bump)
    bump()
    return () => {
      map.off('zoomend', bump)
    }
  }, [map])

  const layout = useMemo(() => {
    // zoomVersion invalidates after zoomend (layer-pixel distances change with zoom only).
    void zoomVersion
    return buildMarkerClusters(map, markers, { excludeKeys })
  }, [map, markers, excludeKeys, zoomVersion])

  return (
    <>
      {layout.clusters.map((cluster) => (
        <ClusterMapMarker
          key={cluster.id}
          cluster={cluster}
          viewport={viewport}
          onSelectMember={onSelectMember}
        />
      ))}
      {children(layout)}
    </>
  )
}
